import React from 'react';

/**
 * KVTable — 상세페이지 KV(키-값) 표.
 *
 * variant:
 *   - confirmed: 확인된 정보 (실선 구분, ink 본문)
 *   - pending: 확인 중 정보 (점선 구분, muted 본문, "확인 중" 라벨)
 *
 * 와이어프레임 detail-rich.html / detail-sparse.html 기준.
 */

export interface KVRow {
  key: string;
  value: React.ReactNode;
  /** 행 단위로 강제 pending 처리 (예: 분양가 행은 PriceTag 컴포넌트를 value로 받고 confirmed) */
  pending?: boolean;
}

export type KVTableVariant = 'confirmed' | 'pending';

export interface KVTableProps {
  rows: KVRow[];
  variant?: KVTableVariant;
  /** 섹션 라벨 (예: "현재 확인된 정보" / "아직 확인 중") */
  title?: string;
  className?: string;
}

export const KVTable: React.FC<KVTableProps> = ({
  rows,
  variant = 'confirmed',
  title,
  className = '',
}) => {
  const isPending = variant === 'pending';
  return (
    <div className={className}>
      {title && (
        <h3 className={`mb-2 text-[10.5px] font-extrabold uppercase ${isPending ? 'text-muted' : 'text-accent-dark'}`}>
          {title}
        </h3>
      )}
      <dl
        className={`grid grid-cols-[96px_1fr] gap-x-4 gap-y-2 text-[13px] ${
          isPending ? 'opacity-80' : ''
        }`}
      >
        {rows.map((row, i) => {
          const rowPending = row.pending ?? isPending;
          return (
            <React.Fragment key={i}>
              <dt className="font-semibold text-muted">{row.key}</dt>
              <dd
                className={`font-bold ${rowPending ? 'text-mute2 italic' : 'text-ink'}`}
              >
                {rowPending && !row.value ? (
                  <span className="inline-flex items-center gap-1 rounded-ca-sm border border-dashed border-ca-line-strong px-1.5 py-0.5 text-[11.5px] font-bold text-mute2">
                    확인 중
                  </span>
                ) : (
                  row.value
                )}
              </dd>
            </React.Fragment>
          );
        })}
      </dl>
    </div>
  );
};

export default KVTable;
